import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import api from "../../api";
import { ELLIPSIS, getCompactPageRange } from "../../components/ui/compactPagination";
import { statusLabel, FeedbackDetail } from "../technicians/TechnicianSatisfactionPanel";
import "../technicians/SatisfactionShared.css";
import "./TicketFeedbackFormsTab.css";

// "Ticket Feedback Forms" tab on the shared Assigned Forms page
// (/technician/my-assigned-forms and friends). READ-ONLY tracking of the
// SYSTEM-GENERATED satisfaction feedback forms sent to a REQUESTER when the
// logged-in technician completes an eligible task -- a completely separate
// population from Tab 1 (Assigned Forms, form_assignment_users.user_id =
// this user, forms THEY personally fill).
//
// CRITICAL DISTINCTION (do not blur): for a row here, the logged-in user is
// the SUBJECT being rated (task_satisfaction_requests.technician_user_id),
// never the recipient/respondent (task_satisfaction_requests.
// requester_user_id, who is the one that actually fills the linked
// form_assignment_users row -- see services/taskSatisfactionService.js's
// header comment). That is exactly why `SELECT * FROM form_assignment_users
// WHERE user_id = <this technician>` can correctly return zero rows while
// this tab still has entries -- these forms were never assigned to the
// technician to fill; nothing here is inserted into form_assignment_users
// on the technician's behalf.
//
// Data comes from GET /technician/ticket-feedback-forms, which is always
// scoped server-side to req.user.id (any technician_id is ignored) and does
// the split itself (services/taskSatisfactionService.js#getTicketFeedbackForms):
//   view=scored   -- submitted responses with marks (answered ratings only);
//   view=awaiting -- "Awaiting Response": request sent, requester hasn't
//                    submitted yet. Legitimate pending data, never scored.
// The header counts come from the same response (meta.counts), so they
// never depend on which view is showing. "View Response" reuses
// GET /technician/satisfaction-summary/feedback/:id via FeedbackDetail.
function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const EMPTY_COUNTS = { total: 0, submitted: 0, scored: 0, submitted_without_ratings: 0, awaiting_response: 0, not_delivered: 0 };

// The endpoint returns the whole period for this one technician, so paging
// is done client-side.
const PAGE_SIZE = 10;

// The technician feedback form (IT_WORK_FEEDBACK_FORM). The endpoint's meta
// carries no form name; a server-sent `meta.form_name` wins if it ever does.
const DEFAULT_FORM_NAME = "IT Work Feedback Form";

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function formatScore(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(1) : "—";
}

// One rating per row. A one-question response's marks and average are the
// same number, so the row shows that single answer. A multi-question
// response shows its average per question; its marks stay in the tooltip.
function rowRating(row) {
  const answered = Number(row.answered_count) || 0;
  if (answered === 1 && row.total_points != null) {
    return { value: row.total_points, max: row.total_max_points ?? null, title: null };
  }
  const perQuestionMax = answered > 1 && row.total_max_points != null ? row.total_max_points / answered : 5;
  return {
    value: row.average_rating,
    max: perQuestionMax,
    title: row.total_points != null ? `Marks ${row.total_points} / ${row.total_max_points ?? "—"}` : null,
  };
}

// Average per answered question across the loaded scored rows -- the same
// "answered ratings only" rule the rows use. Shown only for the period those
// rows were loaded for.
function averageOf(rows) {
  let points = 0;
  let answers = 0;
  const maxima = new Set();
  for (const row of rows) {
    const answered = Number(row.answered_count) || 0;
    if (!answered || row.total_points == null) continue;
    points += Number(row.total_points);
    answers += answered;
    if (row.total_max_points != null) maxima.add(row.total_max_points / answered);
  }
  if (!answers) return null;
  return { value: points / answers, max: maxima.size === 1 ? [...maxima][0] : null };
}

function RatingBadge({ value, max, title }) {
  if (value == null) return <span className="tff-muted">—</span>;
  return (
    <span className="tff-rating" title={title || undefined}>
      <span className="tff-rating-star" aria-hidden="true">★</span>
      {formatScore(value)}
      {max != null && <span className="tff-rating-max"> / {Number(max)}</span>}
    </span>
  );
}

export default function TicketFeedbackFormsTab() {
  const [month, setMonth] = useState(currentMonthValue());
  const [scope, setScope] = useState("monthly"); // "monthly" | "cumulative" -- same two modes as the Dashboard panel
  const [view, setView] = useState("scored"); // "scored" | "awaiting"
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [formName, setFormName] = useState(DEFAULT_FORM_NAME);
  // Average Rating card: { key: "<month>|<scope>", value }, always computed
  // from the period's COMPLETE scored list (never the current page), so
  // paging never moves it. The Awaiting view reuses it for the same period,
  // or loads that period's scored list alongside (see fetchRows).
  const [scoredAverage, setScoredAverage] = useState({ key: null, value: null });
  const scoredAverageRef = useRef(scoredAverage);
  const storeScoredAverage = useCallback((next) => {
    scoredAverageRef.current = next;
    setScoredAverage(next);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const periodKey = `${month}|${scope}`;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const key = `${month}|${scope}`;
    const baseParams = { month, scope: scope === "cumulative" ? "cumulative" : "month" };
    // Awaiting view for a period whose scored list was never loaded: fetch
    // it too (same endpoint, view=scored), only for the Average card. A
    // failure there only leaves the card at "—", never fails the list.
    const scoredForAverage = view === "awaiting" && scoredAverageRef.current.key !== key
      ? api.get("/technician/ticket-feedback-forms", { params: { ...baseParams, view: "scored" } }).catch(() => null)
      : null;
    try {
      const res = await api.get("/technician/ticket-feedback-forms", { params: { ...baseParams, view } });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setCurrentPage(1);
      setExpandedRequestId(null);
      setCounts({ ...EMPTY_COUNTS, ...(res.data?.meta?.counts || {}) });
      setFormName(res.data?.meta?.form_name || DEFAULT_FORM_NAME);
      if (view === "scored") {
        storeScoredAverage({ key, value: averageOf(data) });
      } else if (scoredForAverage) {
        const scoredRes = await scoredForAverage;
        if (scoredRes) {
          const scoredRows = Array.isArray(scoredRes.data?.data) ? scoredRes.data.data : [];
          storeScoredAverage({ key, value: averageOf(scoredRows) });
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load ticket feedback forms.");
      setRows([]);
      setCounts(EMPTY_COUNTS);
    } finally {
      setLoading(false);
    }
  }, [month, scope, view, storeScoredAverage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const isScored = view === "scored";
  const totalPages = Math.max(Math.ceil(rows.length / PAGE_SIZE), 1);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = useMemo(() => rows.slice(pageStart, pageStart + PAGE_SIZE), [rows, pageStart]);
  const visiblePages = getCompactPageRange(currentPage, totalPages).filter((entry) => entry !== ELLIPSIS);
  const average = scoredAverage.key === periodKey ? scoredAverage.value : null;
  const columnCount = isScored ? 7 : 6;

  const goToPage = (page) => {
    setCurrentPage(page);
    setExpandedRequestId(null);
  };

  return (
    <section className="rrf-list-card tff-card">
      <div className="tff-header">
        <div className="tff-title">
          <h5>Ticket Feedback Forms</h5>
          <span>View feedback submitted by requesters for your completed tickets.</span>
        </div>

        <div className="tff-filters">
          <select
            className="rrf-select tff-select"
            value={view}
            onChange={(e) => { setView(e.target.value); setExpandedRequestId(null); }}
            aria-label="Feedback view"
          >
            <option value="scored">Submitted (scored)</option>
            <option value="awaiting">Awaiting Response</option>
          </select>
          <input
            type="month"
            className="rrf-select tff-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Month"
          />
          <select
            className="rrf-select tff-select"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            aria-label="Period"
          >
            <option value="monthly">This Month</option>
            <option value="cumulative">Cumulative</option>
          </select>
        </div>
      </div>

      <div className="tff-summary">
        <div className="tff-form-label">
          Feedback Form: <strong>{formName}</strong>
        </div>

        {!loading && !error && counts.total > 0 && (
          <div className="tff-chips" data-testid="ticket-feedback-counts">
            <div className="tff-chip">
              <span className="tff-chip-label">Total Feedback</span>
              <span className="tff-chip-value">{counts.total}</span>
            </div>
            <div className="tff-chip">
              <span className="tff-chip-label">Submitted</span>
              <span className="tff-chip-value">{counts.submitted}</span>
            </div>
            <div className="tff-chip">
              <span className="tff-chip-label">Awaiting Response</span>
              <span className="tff-chip-value">{counts.awaiting_response}</span>
            </div>
            <div className="tff-chip tff-chip--accent" title="Average of submitted & scored responses for this period">
              <span className="tff-chip-label">Average Rating</span>
              <span className="tff-chip-value">
                {average ? <>{formatScore(average.value)}{average.max != null && <small> / {average.max}</small>}</> : "—"}
              </span>
            </div>
            {counts.not_delivered > 0 && (
              <div className="tff-chip tff-chip--muted">
                <span className="tff-chip-label">Not Delivered</span>
                <span className="tff-chip-value">{counts.not_delivered}</span>
              </div>
            )}
            {counts.submitted_without_ratings > 0 && (
              <div className="tff-chip tff-chip--muted" title="Marks come from submitted & scored responses only">
                <span className="tff-chip-label">Without Ratings</span>
                <span className="tff-chip-value">{counts.submitted_without_ratings}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rrf-table-wrap">
        {loading ? (
          <div className="rrf-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading ticket feedback forms...</span>
          </div>
        ) : error ? (
          <div className="rrf-empty">
            <p>{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rrf-empty">
            <h5>{isScored ? "No submitted feedback yet" : "No feedback awaiting response"}</h5>
            <p>
              {isScored
                ? "No requester has submitted feedback for tickets you’ve completed in this period."
                : "No feedback request for tickets you’ve completed in this period is waiting on the requester."}
            </p>
          </div>
        ) : (
          <div className="tff-table-wrap">
            <table className="tff-table">
              <colgroup>
                <col className="tff-col-sno" />
                <col className="tff-col-ticket" />
                <col className="tff-col-requester" />
                <col className="tff-col-date" />
                {isScored ? (
                  <>
                    <col className="tff-col-date" />
                    <col className="tff-col-rating" />
                  </>
                ) : (
                  <col className="tff-col-status" />
                )}
                <col className="tff-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th className="tff-center">S.No.</th>
                  <th>Ticket</th>
                  <th>Requester</th>
                  <th>Completed</th>
                  {isScored ? (
                    <>
                      <th>Submitted</th>
                      <th>Rating</th>
                    </>
                  ) : (
                    <th>Status</th>
                  )}
                  <th className="tff-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => {
                  const expanded = isScored && expandedRequestId === row.request_id && row.submission_id;
                  return (
                    <React.Fragment key={row.request_id}>
                      <tr className={expanded ? "tff-row--expanded" : undefined}>
                        <td className="tff-center tff-muted">{pageStart + index + 1}</td>
                        <td>
                          <div className="tff-ticket">
                            <span className="tff-ticket-no">#{row.task_id}</span>{" "}
                            <span className="tff-ticket-title" title={row.task_title || undefined}>{row.task_title}</span>
                          </div>
                        </td>
                        <td className="tff-requester">{row.requester_name || "—"}</td>
                        <td className="tff-date">{formatDate(row.completed_at)}</td>
                        {isScored ? (
                          <>
                            <td className="tff-date">{formatDate(row.submitted_at)}</td>
                            <td><RatingBadge {...rowRating(row)} /></td>
                          </>
                        ) : (
                          <td>
                            <span className={`tsp-status-pill tsp-status-pill--${row.status.toLowerCase()}`}>
                              {statusLabel(row.status)}
                            </span>
                          </td>
                        )}
                        <td className="tff-center">
                          {isScored && row.submission_id && (
                            <button
                              type="button"
                              className={`tff-view-btn${expanded ? " is-active" : ""}`}
                              aria-expanded={Boolean(expanded)}
                              onClick={() => setExpandedRequestId(expanded ? null : row.request_id)}
                            >
                              {expanded ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
                              {expanded ? "Hide" : "View"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="tff-detail-row">
                          <td colSpan={columnCount}>
                            <div className="tff-detail-inset">
                              <FeedbackDetail submissionId={row.submission_id} compact />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && rows.length > 0 && (
        <div className="rrf-table-footer tff-footer">
          <div className="rrf-record-info">
            Showing <strong>{pageStart + 1}</strong>&ndash;<strong>{pageStart + pageRows.length}</strong> of{" "}
            <strong>{rows.length}</strong> feedback forms
          </div>

          {totalPages > 1 && (
            <div className="rrf-pagination tff-pagination">
              <button type="button" aria-label="Previous page" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>
                ‹
              </button>
              {visiblePages.map((page, index) => {
                const previousPage = visiblePages[index - 1];
                const showGap = previousPage && page - previousPage > 1;
                return (
                  <React.Fragment key={page}>
                    {showGap && <span className="rrf-page-gap">…</span>}
                    <button
                      type="button"
                      className={page === currentPage ? "active" : ""}
                      aria-current={page === currentPage ? "page" : undefined}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
              <button type="button" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
